import React, { useEffect, useState } from 'react';
import PartnerLayout from '../../components/layout/PartnerLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { DollarSign, Clock, CheckCircle2 } from 'lucide-react';
import { partnerAPI } from '../../services/api';
import { toast } from 'sonner';

const COMMISSION_STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700'
};

export default function PartnerCommissions() {
  const [stats, setStats] = useState({ totalEarned: 0, pendingAmount: 0, totalCommissions: 0 });
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, commissionsRes] = await Promise.all([
        partnerAPI.getCommissionStats(),
        partnerAPI.getCommissions({ page, limit: 20 })
      ]);
      setStats(statsRes);
      setCommissions(commissionsRes.data);
      setPagination(commissionsRes.pagination);
    } catch (error) {
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Commissions</h1>
          <p className="mt-1 text-slate-600">Track your partner earnings</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="partner-card rounded-[26px] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Earned</p>
                <p className="text-xl font-bold text-slate-900">${stats.totalEarned.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="partner-card rounded-[26px] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-xl font-bold text-slate-900">${stats.pendingAmount.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="partner-card rounded-[26px] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Commissions</p>
                <p className="text-xl font-bold text-slate-900">{stats.totalCommissions}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Commission List */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : commissions.length === 0 ? (
          <Card className="partner-card rounded-[28px] p-12 text-center">
            <DollarSign className="mx-auto mb-3 h-12 w-12 text-[#A87DFA]" />
            <h3 className="text-lg font-semibold text-slate-900">No commissions yet</h3>
            <p className="mt-1 text-sm text-slate-600">Commissions will appear as services are completed</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {commissions.map((commission) => (
              <Card key={commission.commissionId} className="partner-card rounded-[26px] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900">
                        {commission.commissionType === 'APPLICATION' ? 'Application' : 'Service'} Commission
                      </span>
                      <Badge className={COMMISSION_STATUS_STYLES[commission.status] || 'bg-gray-100 text-gray-700'}>
                        {commission.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <span>Ref: {commission.referenceId}</span>
                      <span>Rate: {commission.percentage}%</span>
                      <span>{new Date(commission.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-600">${commission.amount.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Base: ${commission.baseAmount.toLocaleString()}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-slate-600">Page {page} of {pagination.totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </PartnerLayout>
  );
}
